import { useEffect } from "react";
import { site } from "../data/site";

type SEOProps = {
  title: string;
  description: string;
  canonical?: string | null;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
};

const absoluteUrl = (path = "/") =>
  path.startsWith("http") ? path : `${site.domain}${path.startsWith("/") ? path : `/${path}`}`;

function setMeta(selector: string, attrs: Record<string, string>) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement(attrs.property ? "meta" : attrs.rel ? "link" : "meta");
    document.head.appendChild(node);
  }

  Object.entries(attrs).forEach(([key, value]) => node?.setAttribute(key, value));
}

export default function SEO({
  title,
  description,
  canonical,
  image = site.heroImages[0],
  jsonLd,
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    const canonicalUrl = canonical === undefined ? site.domain : canonical;
    document.title = title;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow",
    });
    if (canonicalUrl) {
      setMeta('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });
      setMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:image"]', { property: "og:image", content: absoluteUrl(image) });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });

    document.querySelectorAll('script[data-polishing-jsonld="true"]').forEach((node) => node.remove());
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.polishingJsonld = "true";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, canonical, image, jsonLd, noindex]);

  return null;
}
